import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Calculator,
  Package,
  CheckSquare,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Clock,
  Bell
} from 'lucide-react';
import { format } from 'date-fns';
import { customerAPI, orderAPI, todoAPI, stockAPI, employeeAPI, attendanceAPI, remainderAPI } from '@/lib/api';

const Home = ({ user }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    customers: 0,
    orders: 0,
    employees: 0,
    todosTotal: 0,
    todosPending: 0,
    stocksTotal: 0,
    stocksLowStock: 0,
    attendanceToday: 0,
    attendancePresent: 0,
    recentOrders: [],
    lowStockItems: [],
    pendingTodos: [],
    todayReminders: [],
    pendingRemainders: []
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const [customers, orders, employees, todos, stocks, attendance, todayReminders, pendingRemainders] = await Promise.all([
        customerAPI.getAll().catch(() => []),
        orderAPI.getAll().catch(() => []),
        employeeAPI.getAll().catch(() => []),
        todoAPI.getAll().catch(() => []),
        stockAPI.getAll().catch(() => []),
        attendanceAPI.getAll(today).catch(() => []),
        stockAPI.getTodayReminders().catch(() => []),
        remainderAPI.getPending().catch(() => [])
      ]);

      // Calculate statistics
      const pendingTodos = todos.filter(t => t.status === 'Pending');
      const lowStockItems = stocks.filter(s => s.reorder_point && s.quantity <= s.reorder_point);
      const presentToday = attendance.filter(a => a.status === 'Present').length;
      const recentOrders = orders.slice(0, 5); // Get last 5 orders

      setDashboardData({
        customers: customers.length,
        orders: orders.length,
        employees: employees.length,
        todosTotal: todos.length,
        todosPending: pendingTodos.length,
        stocksTotal: stocks.length,
        stocksLowStock: lowStockItems.length,
        attendanceToday: attendance.length,
        attendancePresent: presentToday,
        recentOrders,
        lowStockItems: lowStockItems.slice(0, 5),
        pendingTodos: pendingTodos.slice(0, 5),
        todayReminders,
        pendingRemainders
      });
      
      // Show notification if there are reminders today
      if (todayReminders.length > 0) {
        const reminderMessages = todayReminders.map(r => `• ${r.item_name}`).join('\n');
        console.log(`📢 You have ${todayReminders.length} reminder(s) today:\n${reminderMessages}`);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    {
      title: 'Total Orders',
      value: dashboardData.orders,
      subtitle: 'All time',
      icon: ShoppingBag,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      trend: null
    },
    {
      title: 'Customers',
      value: dashboardData.customers,
      subtitle: 'In database',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      trend: null
    },
    {
      title: 'Today\'s Attendance',
      value: `${dashboardData.attendancePresent}/${dashboardData.attendanceToday}`,
      subtitle: 'Present today',
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      trend: null
    },
    {
      title: 'Pending Remainders',
      value: dashboardData.pendingRemainders.length,
      subtitle: 'Due/Overdue',
      icon: Clock,
      color: dashboardData.pendingRemainders.length > 0 ? 'text-red-600' : 'text-orange-600',
      bgColor: dashboardData.pendingRemainders.length > 0 ? 'bg-red-100' : 'bg-orange-100',
      trend: null
    }
  ];

  const quickActions = [
    {
      title: 'Gold Calculator',
      description: 'Calculate gold values and prices',
      icon: Calculator,
      path: '/gold-calculator',
      color: 'bg-accent text-accent-foreground hover:bg-accent-hover'
    },
    {
      title: 'Order Status',
      description: 'Track and manage orders',
      icon: Package,
      path: '/order-status',
      color: 'bg-primary text-primary-foreground hover:bg-primary-hover'
    },
    {
      title: 'Attendance',
      description: 'Mark daily attendance',
      icon: Users,
      path: '/attendance',
      color: 'bg-secondary text-secondary-foreground hover:bg-secondary-hover'
    },
    {
      title: 'Remainder',
      description: 'Maintenance schedules',
      icon: Clock,
      path: '/remainder',
      color: 'bg-primary text-primary-foreground hover:bg-primary-hover'
    }
  ];

  const getOrderStatusBadge = (status) => {
    const variants = {
      'Pending': 'outline',
      'In Progress': 'secondary',
      'Completed': 'default',
      'Delivered': 'default'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold mb-2">
          Welcome back, <span className="bg-gradient-primary bg-clip-text text-transparent">{user?.username}</span>!
        </h1>
        <p className="text-muted-foreground text-lg">Here's what's happening with your business today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-1">{stat.value}</h3>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                {stat.subtitle && (
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Today's Reminders Section */}
      {dashboardData.todayReminders.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Bell className="h-5 w-5" />
              Today's Reminders
            </CardTitle>
            <CardDescription>You have {dashboardData.todayReminders.length} reminder(s) for stock/indent items today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData.todayReminders.map((reminder, index) => (
                <div key={index} className="flex items-start justify-between py-3 px-4 bg-white rounded-lg border border-blue-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900">{reminder.item_name}</p>
                      <Badge variant="outline" className="text-xs">{reminder.category}</Badge>
                    </div>
                    {reminder.reminder_notes && (
                      <p className="text-sm text-gray-600 mt-1">{reminder.reminder_notes}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span>Stock: {reminder.quantity} {reminder.unit}</span>
                      {reminder.supplier && <span>• Supplier: {reminder.supplier}</span>}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/stocks-indent')}
                    className="ml-2"
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
            <Button 
              variant="default" 
              className="w-full mt-4"
              onClick={() => navigate('/stocks-indent')}
            >
              Go to Stock/Indent
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pending Remainders Section - Highlighted in Red */}
      {dashboardData.pendingRemainders.length > 0 && (
        <Card className="border-red-300 bg-red-50/50" data-testid="pending-remainders-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5 animate-pulse" />
              Pending Jobs - Maintenance Due
            </CardTitle>
            <CardDescription className="text-red-600">
              {dashboardData.pendingRemainders.length} maintenance task(s) overdue or due today
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData.pendingRemainders.map((remainder, index) => (
                <div 
                  key={index} 
                  className="flex items-start justify-between py-3 px-4 bg-white rounded-lg border border-red-200 hover:border-red-300 transition-colors"
                  data-testid={`pending-remainder-${remainder.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-red-800">{remainder.title}</p>
                      <Badge className="bg-red-100 text-red-800 text-xs">{remainder.cycle}</Badge>
                    </div>
                    {remainder.remarks && (
                      <p className="text-sm text-gray-600 mt-1">{remainder.remarks}</p>
                    )}
                    <p className="text-xs text-red-600 mt-2 font-medium">
                      Due: {remainder.next_due_date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              variant="destructive" 
              className="w-full mt-4"
              onClick={() => navigate('/remainder')}
            >
              Go to Remainder
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Alerts Section */}
      {(dashboardData.lowStockItems.length > 0 || dashboardData.todosPending > 5) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Low Stock Alert */}
          {dashboardData.lowStockItems.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle className="h-5 w-5" />
                  Low Stock Alerts
                </CardTitle>
                <CardDescription>{dashboardData.stocksLowStock} items need restocking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.lowStockItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                      <div>
                        <p className="font-medium">{item.item_name}</p>
                        <p className="text-sm text-muted-foreground">{item.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-orange-600">
                          {item.quantity} {item.unit}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Reorder at {item.reorder_point}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => navigate('/stocks-indent')}
                >
                  View All Stock
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pending Tasks */}
          {dashboardData.pendingTodos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Pending Tasks
                </CardTitle>
                <CardDescription>{dashboardData.todosPending} tasks pending</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.pendingTodos.map((todo, index) => (
                    <div key={index} className="flex items-start justify-between py-2 border-b last:border-b-0">
                      <div className="flex-1">
                        <p className="font-medium">{todo.title}</p>
                        {todo.due_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Due: {todo.due_date}
                          </p>
                        )}
                      </div>
                      <Badge variant={todo.priority === 'High' ? 'destructive' : 'secondary'}>
                        {todo.priority}
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => navigate('/todo-list')}
                >
                  View All Tasks
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Card
                key={index}
                className="group cursor-pointer hover:shadow-emerald transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                onClick={() => navigate(action.path)}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                  <CardTitle className="text-lg">{action.title}</CardTitle>
                  <CardDescription>{action.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Orders */}
      {dashboardData.recentOrders.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Recent Orders</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {dashboardData.recentOrders.map((order, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{order.item_description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {order.order_date && (
                        <span className="text-sm text-muted-foreground">{order.order_date}</span>
                      )}
                      {getOrderStatusBadge(order.status)}
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/order-status')}
              >
                View All Orders
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Employee Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Employee Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Employees</span>
                <span className="font-bold">{dashboardData.employees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Present Today</span>
                <span className="font-bold text-green-600">{dashboardData.attendancePresent}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Not Marked</span>
                <span className="font-bold text-orange-600">
                  {dashboardData.employees - dashboardData.attendanceToday}
                </span>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/attendance')}
            >
              Manage Attendance
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inventory Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Items</span>
                <span className="font-bold">{dashboardData.stocksTotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Low Stock</span>
                <span className="font-bold text-orange-600">{dashboardData.stocksLowStock}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">In Stock</span>
                <span className="font-bold text-green-600">
                  {dashboardData.stocksTotal - dashboardData.stocksLowStock}
                </span>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/stocks-indent')}
            >
              Manage Inventory
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Customers</span>
                <span className="font-bold">{dashboardData.customers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Orders</span>
                <span className="font-bold">{dashboardData.orders}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pending Tasks</span>
                <span className="font-bold">{dashboardData.todosPending}</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/reports')}
            >
              View Reports
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Home;